import React, { useCallback, forwardRef }from 'react';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import _noop from 'lodash/noop';

import {
    useModalStore,
    useReelStore,
    useUserData,
} from '../hooks/store';
import {
    useMediaConfig,
    useDictionary,
} from '../hooks/config';

import { constants as modalConstants } from 'bdvp-graphql/src/resolvers/modals';
import { useClipTrimming } from 'bdvp-common/src/hooks';

import Button from '@mlb/react-patterns/components/Button/button.tsx';

const { REEL_MODAL_NAME } = modalConstants;

const ReelButton = forwardRef(({
    mediaPlayback,
    onClick,
    ...otherProps
}, ref) => {

    const config = useMediaConfig();
    const dictionary = useDictionary();

    const mediaId = _get(mediaPlayback, 'id', '');
    const title = _get(mediaPlayback, 'title', '');
    const clipSlug = _get(mediaPlayback, 'slug', '');
    const feed = _get(mediaPlayback, 'feeds[0]', {});
    const feedType = _get(feed, 'type', '');
    const imageTemplate = _get(feed, 'image.templateUrl', '');
    const { segments = [] } = useClipTrimming(mediaPlayback, config);
    const originalDuration = segments[segments.length - 1] || 0;
    const isInReel = _get(mediaPlayback, 'isInReel', false);

    const {
        data: reelData,
        addReelClip,
        removeReelClip,
    } = useReelStore();
    const { modalShow } = useModalStore();

    const { data: userData = {} } = useUserData();

    const onClickHandler = useCallback(() => {

        onClick();

        if(userData.isLoggedIn) {

            if (isInReel) {

                removeReelClip(mediaId);
                return;

            }

            const maxClips = _get(config, 'reels.creation.maxClips', 10);
            const reelClips = _get(reelData, 'reel.reelClips', []);

            if (reelClips.length >= maxClips - 1) {

                modalShow(REEL_MODAL_NAME);

            };

            if (reelClips.length === maxClips) {

                return;

            }

            addReelClip({
                mediaId,
                title,
                clipSlug,
                feedType,
                thumbnailUrl: imageTemplate,
                originalDuration,
            });

        } else {

            const queryParams = {
                redirectUri: encodeURIComponent(window.location.href),
            };

            const queryString = Object.keys(queryParams)
                .map((key) => `${key}=${queryParams[key]}`)
                .join('&');

            window.location.href = `${_get(config, 'loginBaseUrl', 'https://www.mlb.com/login')}?${queryString}`;

        }

    }, [
        addReelClip,
        removeReelClip,
        mediaId,
        title,
        clipSlug,
        feedType,
        imageTemplate,
        originalDuration,
        config,
        isInReel,
        reelData,
        modalShow,
        onClick,
        userData.isLoggedIn,
    ]);

    const ariaLabel = isInReel
        ? _get(dictionary, 'remove_from_reel_text', 'Remove from Reel')
        : _get(dictionary, 'add_to_reel_text', 'Add to Reel');

    return (
        <Button
            label={_get(dictionary, 'reels_text', 'Reels')}
            ariaLabel={ariaLabel}
            handlers={{ onClick: onClickHandler }}
            ref={ref}
            {...otherProps}
        />
    );

});

ReelButton.propTypes = {
    mediaPlayback: PropTypes.object.isRequired,
    onClick: PropTypes.func,
};

ReelButton.defaultProps = {
    onClick: _noop,
};

export default ReelButton;
